// Remembers that a patient's visit note has already been read for orders.
//
// The Orders & followups modal is remounted on every open, and it used to
// re-run the note scan each time — two AI calls and a "Reading the visit
// note…" spinner — for a note that had not changed since the last read. The
// backend scan is idempotent, so the only thing a repeat buys is the wait.
//
// The key is a hash of the note's live editor contents, so an edited note is
// read again on the next open and an untouched one is not.
//
// Lifetime: survives a normal refresh (sessionStorage), NOT a hard refresh.
// A hard refresh is the start-over gesture, and it is also how this gets
// tested. sessionStorage itself outlives both, so the hard refresh is
// detected instead — see `wasHardRefresh` — and the marks dropped on the
// first read after one.

const memory = new Map();
const PREFIX = "orders.noteScan.";
let hardRefreshChecked = false;

// djb2 over the JSON of the delta. This is a cache key, not a fingerprint —
// it only has to change when the text does.
export function hashNoteDelta(delta) {
  const text = JSON.stringify(delta ?? null);
  let h = 5381;
  for (let i = 0; i < text.length; i += 1) {
    h = ((h << 5) + h + text.charCodeAt(i)) | 0; // eslint-disable-line no-bitwise
  }
  return `${text.length}:${(h >>> 0).toString(36)}`; // eslint-disable-line no-bitwise
}

/**
 * Whether this page load was a HARD refresh (Ctrl/Cmd+Shift+R).
 *
 * Nothing announces it, but it leaves a fingerprint: a hard refresh bypasses
 * the HTTP cache, so the largest script arrives in full — transferSize at or
 * above its encoded body size. A normal reload revalidates it instead.
 *
 * Measured in Chrome against the dev server (bundle.js, gzipped):
 *   normal reload  transferSize 300        encodedBodySize 0          → 304
 *   hard reload    transferSize 5,208,452  encodedBodySize 5,208,152  → 200
 * Chrome reports the body as 0 on a 304 (nothing was transferred), so a
 * revalidated script drops out of the "sized" set and the answer is "not
 * hard" — the same answer a memory-cache hit (0/0) gets. Other browsers may
 * report the cached size on a 304 with a tiny transfer; that reads as "not
 * hard" too. A first visit is type "navigate" and has no marks to drop.
 *
 * `perf` is injectable for tests; production callers pass nothing.
 */
export function wasHardRefresh(perf = typeof performance !== "undefined" ? performance : null) {
  try {
    const nav = perf?.getEntriesByType?.("navigation")?.[0];
    if (!nav || nav.type !== "reload") return false;
    const scripts = (perf.getEntriesByType("resource") || []).filter(
      (r) => r.initiatorType === "script" && r.encodedBodySize > 0
    );
    if (scripts.length === 0) return false;
    const biggest = scripts.reduce((a, b) => (b.encodedBodySize > a.encodedBodySize ? b : a));
    return biggest.transferSize >= biggest.encodedBodySize;
  } catch (_) {
    return false;
  }
}

function storageKeys() {
  try {
    return Object.keys(window.sessionStorage).filter((k) => k.startsWith(PREFIX));
  } catch (_) { return []; }
}
function storageGet(key) {
  try { return window.sessionStorage.getItem(PREFIX + key); } catch (_) { return null; }
}
function storageSet(key, value) {
  try { window.sessionStorage.setItem(PREFIX + key, value); } catch (_) { /* private mode etc. */ }
}
function storageDel(key) {
  try { window.sessionStorage.removeItem(PREFIX + key); } catch (_) { /* ditto */ }
}

// Runs once per page, on the first touch of the cache — by which point every
// script has loaded and Resource Timing has the numbers to read.
function dropMarksIfHardRefreshed() {
  if (hardRefreshChecked) return;
  hardRefreshChecked = true;
  if (!wasHardRefresh()) return;
  const keys = storageKeys();
  keys.forEach((k) => { try { window.sessionStorage.removeItem(k); } catch (_) { /* */ } });
  memory.clear();
  console.log(`[Orders] hard refresh detected — note-read cache cleared (${keys.length} mark(s))`);
}

/** The note hash last scanned for this patient, or null. */
export function noteScanMark(patientId) {
  dropMarksIfHardRefreshed();
  const id = String(patientId || "");
  if (!id) return null;
  if (memory.has(id)) return memory.get(id);
  const stored = storageGet(id);
  if (stored) memory.set(id, stored);
  return stored || null;
}

export function rememberNoteScan(patientId, hash) {
  dropMarksIfHardRefreshed();
  const id = String(patientId || "");
  if (!id || !hash) return;
  memory.set(id, hash);
  storageSet(id, hash);
}

export function forgetNoteScan(patientId) {
  const id = String(patientId || "");
  if (!id) return;
  memory.delete(id);
  storageDel(id);
}

/** Tests only: re-arm the once-per-page hard-refresh check. */
export function _resetHardRefreshCheck() {
  hardRefreshChecked = false;
}
